import { NmLoader } from '../../components/NmLoader';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { NmButton } from '../../components/NmButton';
import { NmInput } from '../../components/NmInput';
import {
  calendar,
  type AthleteItem,
  type CalendarWorkout,
} from '../../services/api';
import { formatApiDateCompact } from '../../utils/calendarPickerLocale';

import type { WorkoutSummary } from './types';

interface WorkoutPickerModalProps {
  open: boolean;
  /** Athlete whose workouts are listed (also stored on selection). */
  athleteUserId: number;
  isCoach?: boolean;
  athletes?: AthleteItem[];
  athleteLabel?: string;
  slotLabel?: string;
  excludeId?: number;
  onClose: () => void;
  onSelect: (workout: WorkoutSummary) => void;
}

function toSummary(w: CalendarWorkout, userId: number): WorkoutSummary {
  return {
    id: w.id,
    userId,
    title: w.title ?? '',
    workout_day: w.workout_day ?? '',
    total_time: w.total_time ?? null,
    distance: w.distance ?? null,
    tss_actual: w.tss_actual ?? null,
    workout_type: w.workout_type,
  };
}

function dateRange(days: number): { from: string; to: string } {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - days);
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { from: fmt(from), to: fmt(to) };
}

export function WorkoutPickerModal({
  open,
  athleteUserId: initialAthleteUserId,
  isCoach = false,
  athletes = [],
  athleteLabel,
  slotLabel,
  excludeId,
  onClose,
  onSelect,
}: WorkoutPickerModalProps) {
  const { t, i18n } = useTranslation();

  const [search, setSearch] = useState('');
  const [recentList, setRecentList] = useState<WorkoutSummary[]>([]);
  const [searchResults, setSearchResults] = useState<WorkoutSummary[] | null>(null);
  const [recentLoading, setRecentLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [pickerAthleteId, setPickerAthleteId] = useState(initialAthleteUserId);

  useEffect(() => {
    if (open) {
      setPickerAthleteId(initialAthleteUserId);
      setSearch('');
      setSearchResults(null);
    }
  }, [open, initialAthleteUserId]);

  const activeAthleteId = isCoach && athletes.length > 0 ? pickerAthleteId : initialAthleteUserId;
  const searchQuery = search.trim();
  const isSearching = searchQuery.length > 0;

  useEffect(() => {
    if (!open || !activeAthleteId) return;

    let cancelled = false;
    setRecentLoading(true);

    const { from, to } = dateRange(90);
    void calendar
      .getCalendarData(activeAthleteId, from, to)
      .then(res => {
        if (cancelled) return;
        const days = res.data?.data ?? [];
        const items: WorkoutSummary[] = [];
        for (const day of days) {
          for (const w of day.workouts ?? []) {
            if (w.total_time != null && w.id !== excludeId) {
              items.push(toSummary(w, activeAthleteId));
            }
          }
        }
        items.sort((a, b) => (b.workout_day ?? '').localeCompare(a.workout_day ?? ''));
        setRecentList(items);
      })
      .finally(() => {
        if (!cancelled) setRecentLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, activeAthleteId, excludeId]);

  useEffect(() => {
    if (!open || !activeAthleteId || !isSearching) {
      setSearchResults(null);
      setSearchLoading(false);
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(() => {
      setSearchLoading(true);
      void calendar
        .searchCompletedWorkouts(activeAthleteId, searchQuery, excludeId)
        .then(res => {
          if (cancelled) return;
          const rows = res.data?.data?.workouts ?? [];
          setSearchResults(rows.map(w => toSummary(w, activeAthleteId)));
        })
        .catch(() => {
          if (!cancelled) setSearchResults([]);
        })
        .finally(() => {
          if (!cancelled) setSearchLoading(false);
        });
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [open, activeAthleteId, excludeId, isSearching, searchQuery]);

  const displayList = isSearching ? (searchResults ?? []) : recentList;
  const listLoading = isSearching ? searchLoading : recentLoading;

  const activeAthleteName = athletes.find(a => a.id === activeAthleteId)?.full_name;

  if (!open) return null;

  return (
    <div style={overlay} onClick={onClose}>
      <div className="modal-surface" style={modal} onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 4px', fontSize: 16 }}>
          {slotLabel ? t('compare.picker.pickSlot', { slot: slotLabel }) : t('compare.picker.pickCompleted')}
        </h3>

        {isCoach && athletes.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--fg-3)', marginBottom: 4 }}>
              {t('compare.picker.athlete')}
            </label>
            <select
              value={pickerAthleteId}
              onChange={e => setPickerAthleteId(Number(e.target.value))}
              style={selectStyle}
            >
              {athletes.map(a => (
                <option key={a.id} value={a.id}>
                  {a.full_name}
                </option>
              ))}
            </select>
          </div>
        )}

        {!isCoach && athleteLabel && (
          <p style={{ margin: '0 0 12px', fontSize: 11, color: 'var(--fg-3)' }}>{athleteLabel}</p>
        )}

        {isCoach && activeAthleteName && (
          <p style={{ margin: '0 0 12px', fontSize: 11, color: 'var(--fg-3)' }}>
            {isSearching
              ? t('compare.picker.searchAllFor', { name: activeAthleteName })
              : t('compare.picker.completedFor', { name: activeAthleteName })}
          </p>
        )}

        <NmInput value={search} onChange={e => setSearch(e.target.value)} placeholder={t('compare.picker.searchPlaceholder')} />

        <div style={{ maxHeight: 360, overflow: 'auto', marginTop: 12 }}>
          {listLoading && <p style={{ color: 'var(--fg-3)' }}><NmLoader size="sm" inline label={t('compare.picker.loading')} /></p>}
          {!listLoading && displayList.length === 0 && (
            <p style={{ color: 'var(--fg-3)' }}>
              {isSearching ? t('compare.picker.noSearchResults') : t('compare.picker.noWorkouts')}
            </p>
          )}
          {displayList.map(w => (
            <button
              key={`${w.userId}-${w.id}`}
              type="button"
              onClick={() => {
                onSelect(w);
                onClose();
              }}
              style={rowBtn}
            >
              <span style={{ fontWeight: 600 }}>{w.title || t('compare.workoutFallback')}</span>
              <span className="ltr-island" style={{ fontSize: 11, color: 'var(--fg-3)' }}>
                {formatApiDateCompact(w.workout_day, i18n.language)}
              </span>
            </button>
          ))}
        </div>

        <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
          <NmButton variant="ghost" onClick={onClose}>
            Cancel
          </NmButton>
        </div>
      </div>
    </div>
  );
}

const overlay: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(26,31,43,.45)',
  display: 'grid',
  placeItems: 'center',
  zIndex: 9000,
};

const modal: React.CSSProperties = {
  width: 'min(480px, 92vw)',
  background: 'var(--surface-0)',
  borderRadius: 'var(--r-4)',
  boxShadow: 'var(--nm-raised-md)',
  padding: 20,
};

const rowBtn: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  width: '100%',
  padding: '10px 12px',
  marginBottom: 6,
  border: 'none',
  borderRadius: 'var(--r-2)',
  background: 'var(--surface-sunken)',
  cursor: 'pointer',
  textAlign: 'start',
};

const selectStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 'var(--r-2)',
  border: 'none',
  boxShadow: 'var(--nm-inset-sm)',
  background: 'var(--surface-sunken)',
  fontSize: 13,
  color: 'var(--fg-1)',
};
